import React, { useState, useEffect, createContext, useContext } from 'react'
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom'
import Dashboard from './pages/Dashboard'
import WeekOverWeek from './pages/WeekOverWeek'
import WorkItems from './pages/WorkItems'
import ExecutiveTracker from './pages/ExecutiveTracker'
import Members from './pages/Members'
import Admin from './pages/Admin'
import Login from './pages/Login'
import FeatureParity from './pages/FeatureParity'
import { api } from './api'

const AuthContext = createContext(null)
export const useAuth = () => useContext(AuthContext)

const TAB_ROUTES = {
  'Dashboard': '/dashboard',
  'Week Over Week': '/week-over-week',
  'Work Items': '/work-items',
  'Feature Parity': '/feature-parity',
  'Executive Tracker': '/executive-tracker',
  'Members': '/members',
}

const TAB_LABELS = {
  'Dashboard': '🏠 Cycle Overview',
  'Week Over Week': '📅 Week Over Week',
  'Work Items': '📋 Work Items',
  'Feature Parity': '📊 Feature Parity',
  'Executive Tracker': '🚨 Exec Tracker',
  'Members': '👥 Squad Members',
}

function App() {
  const [user, setUser] = useState(null)
  const [userInfo, setUserInfo] = useState(null)
  const [loading, setLoading] = useState(true)

  const [accessDenied, setAccessDenied] = useState(false)

  useEffect(() => {
    fetch('/.auth/me')
      .then(r => r.json())
      .then(data => {
        if (data.clientPrincipal) {
          setUser(data.clientPrincipal)
          return api.getMe().catch(err => {
            setAccessDenied(true)
            return null
          })
        }
        return null
      })
      .then(info => {
        if (info) setUserInfo(info)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="loading-screen">Loading...</div>
  if (!user) return <Login />
  if (accessDenied) return <AccessDenied username={user.userDetails} />

  const isAdmin = userInfo?.role === 'admin'
  const allowedTabs = isAdmin
    ? ['Dashboard', 'Week Over Week', 'Work Items', 'Feature Parity', 'Executive Tracker', 'Members']
    : (userInfo?.allowedTabs || ['Dashboard', 'Work Items', 'Executive Tracker', 'Members'])

  return (
    <AuthContext.Provider value={{ ...user, userInfo, isAdmin, allowedTabs }}>
      <div className="app">
        <Nav user={user} isAdmin={isAdmin} allowedTabs={allowedTabs} />
        <main className="main-content">
          <Routes>
            {allowedTabs.includes('Dashboard') && <Route path="/dashboard" element={<Dashboard />} />}
            {allowedTabs.includes('Week Over Week') && <Route path="/week-over-week" element={<WeekOverWeek />} />}
            {allowedTabs.includes('Work Items') && <Route path="/work-items" element={<WorkItems />} />}
            {allowedTabs.includes('Feature Parity') && <Route path="/feature-parity" element={<FeatureParity />} />}
            {allowedTabs.includes('Executive Tracker') && <Route path="/executive-tracker" element={<ExecutiveTracker />} />}
            {allowedTabs.includes('Members') && <Route path="/members" element={<Members />} />}
            {isAdmin && <Route path="/admin" element={<Admin />} />}
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </main>
      </div>
    </AuthContext.Provider>
  )
}

function AccessDenied({ username }) {
  // GitHub OAuth logout: redirect to GitHub to revoke, then back to our login
  const switchAccount = () => {
    // Logout from SWA first, then on return page trigger GitHub OAuth with prompt=login
    // This forces GitHub to show the account picker
    window.location.href = '/.auth/logout?post_logout_redirect_uri=/switch'
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <h1>🚫 Access Denied</h1>
        <p style={{ color: '#666', marginBottom: 8 }}>
          The account <strong>{username}</strong> does not have access to Happy Tracker.
        </p>
        <p style={{ color: '#999', fontSize: 14, marginBottom: 24 }}>
          Please contact your admin to get access, or sign in with a different account.
        </p>
        <button onClick={switchAccount} className="github-btn" style={{ cursor: 'pointer', border: 'none' }}>
          🔄 Switch Account
        </button>
        <a href="/.auth/logout?post_logout_redirect_uri=/" className="logout-link" style={{ display: 'block', marginTop: 16, color: '#999', fontSize: 14 }}>
          Sign Out
        </a>
      </div>
    </div>
  )
}

function Nav({ user, isAdmin, allowedTabs }) {
  const loc = useLocation()

  // Refresh stale cache when switching tabs
  useEffect(() => {
    api.refresh()
  }, [loc.pathname])

  const links = allowedTabs
    .filter(tab => TAB_ROUTES[tab])
    .map(tab => ({ to: TAB_ROUTES[tab], label: TAB_LABELS[tab] }))

  if (isAdmin) {
    links.push({ to: '/admin', label: '⚙️ Admin' })
  }

  return (
    <nav className="sidebar">
      <div className="sidebar-header">
        <h1>🎯 Happy Tracker</h1>
      </div>
      <div className="sidebar-links">
        {links.map(l => (
          <Link key={l.to} to={l.to} className={`nav-link ${loc.pathname === l.to ? 'active' : ''}`}>
            {l.label}
          </Link>
        ))}
      </div>
      <div className="sidebar-footer">
        <span className="user-name">{user.userDetails}</span>
        {isAdmin && <span className="admin-badge">Admin</span>}
        <a href="/.auth/logout" className="logout-btn">Logout</a>
      </div>
    </nav>
  )
}

export default App
